import React from "react";
import { createPortal } from "react-dom";
import { Dropdown } from "react-bootstrap";
import { IoIosOptions } from "react-icons/io";
import { Link } from "react-router-dom";

// Renders the menu into document.body instead of in place. Needed so the menu isn't
// clipped when a row's dropdown is opened inside a scrolling/clipped container (e.g. a
// DesktopWindow, whose drag transform also defeats position:fixed's usual ability to
// escape an ancestor's overflow:hidden). Paired with popperConfig's fixed strategy below
// so Popper still positions it correctly now that it's no longer near the toggle in the DOM.
const PortalDropdownMenu = React.forwardRef(
  // react-bootstrap adds `show`/`close`/`align` for custom (non-DOM) menu components —
  // strip them out here rather than spreading them onto the actual <div>.
  ({ children, show, close, align, ...domProps }, ref) =>
    createPortal(
      <div ref={ref} {...domProps}>
        {children}
      </div>,
      document.body
    )
);

const ActionDropdown = ({ actions, item, idColumn, idColumn2 }) => {
  const checkVisibility = (visibilityConditions, dataObject) => {
    // Check if the conditions array is empty
    if (!visibilityConditions || visibilityConditions.length === 0) {
      return true; // If no conditions, consider it visible
    }

    // Evaluate each visibility condition
    return visibilityConditions.every((condition) => {
      const { key, operation, value } = condition;

      // Ensure the key exists in the dataObject
      if (dataObject.hasOwnProperty(key)) {
        // Perform the specified operation
        switch (operation) {
          case ">":
            return dataObject[key] > value;
          case "<":
            return dataObject[key] < value;
          case ">=":
            return dataObject[key] >= value;
          case "<=":
            return dataObject[key] <= value;
          case "==":
            // eslint-disable-next-line eqeqeq
            return dataObject[key] == value; // Loose equality
          case "===":
            return dataObject[key] === value; // Strict equality
          default:
            return false; // Invalid operation
        }
      } else {
        return false; // Key doesn't exist in dataObject
      }
    });
  };

  const handleActionClick = (e, action) => {
    e.stopPropagation();
    if (action.event) {
      action.event(item[idColumn]);
    }
  };

  const renderAction = (action) => {
    const content = (
      <>
        {action.icon} {action.text && <span>{action.text}</span>}
      </>
    );

    if (action.url) {
      return (
        <Link
          to={action.url
            .replace("{id}", item[idColumn])
            .replace("{id2}", item[idColumn2])}
          className="text-decoration-none text-secondary"
          title={action.text}
        >
          {content}
        </Link>
      );
    }

    return (
      <span
        onClick={(e) => handleActionClick(e, action)}
        className="text-decoration-none text-secondary"
        title={action.text}
        style={{ cursor: "pointer" }}
      >
        {content}
      </span>
    );
  };

  if (actions.length === 0) {
    return <></>;
  }

  if (actions.length === 1) {
    if (!checkVisibility(actions[0].visibilityConditions, item)) return <></>;
    return <>{renderAction(actions[0])}</>;
  }

  return (
    <Dropdown align="end">
      <Dropdown.Toggle variant="link" className="text-decoration-none">
        <IoIosOptions className="mb-2" />
      </Dropdown.Toggle>
      <Dropdown.Menu
        as={PortalDropdownMenu}
        popperConfig={{ strategy: "fixed" }}
        className="rounded-0 shadow-lg p-3"
      >
        {actions.map((action, index) => {
          if (!checkVisibility(action.visibilityConditions, item)) return null;
          return (
            <Dropdown.Item key={index} as="div">
              {renderAction(action)}
            </Dropdown.Item>
          );
        })}
      </Dropdown.Menu>
    </Dropdown>
  );
};

export default ActionDropdown;
